import { Address, ChatMessage } from "../types";
import { getAuthHeaders } from "./supabaseService";
import { addressKey } from "../utils/addressKey";

export interface ScanResult {
  address: Address;
  pharmacyName?: string; // naam van de apotheek zoals op het label staat
}

const MODEL = 'gemini-2.5-flash';

/**
 * Stuurt een verzoek naar de Netlify proxy-functie die de Gemini API aanroept.
 * De API key blijft op de server — nooit zichtbaar in de browser.
 */
/**
 * Fout van de Gemini-proxy mét HTTP-status. Zonder de status kan de Scanner geen
 * onderscheid maken tussen een verlopen sessie (401), een rate limit (429) en een
 * echte verwerkingsfout — allemaal zagen ze er hetzelfde uit.
 */
export class GeminiProxyError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'GeminiProxyError';
    this.status = status;
  }
}

const MAX_RETRIES = 3;
// 429 niet meer retrybaar — de proxy doet een eigen rate limit, dus retryen
// is zinloos én vermenigvuldigt het probleem. 503 is wel een tijdelijke
// upstream-glitch die een retry rechtvaardigt.
const RETRYABLE_STATUSES = new Set([503]);

async function callGemini(requestBody: object): Promise<string | null> {
  const body = JSON.stringify({ model: MODEL, ...requestBody });

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30_000);

    let response: Response;
    try {
      response = await fetch('/.netlify/functions/gemini', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store',
          'Pragma': 'no-cache',
          ...(await getAuthHeaders()),
        },
        body,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    // Rate-limit of overload → exponential backoff en opnieuw proberen
    if (RETRYABLE_STATUSES.has(response.status) && attempt < MAX_RETRIES - 1) {
      const waitMs = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
      console.warn(`[Gemini] ${response.status} ontvangen — wacht ${waitMs}ms vóór retry (poging ${attempt + 2}/${MAX_RETRIES})`);
      await new Promise(r => setTimeout(r, waitMs));
      continue;
    }

    if (!response.ok) {
      let errMsg = `Gemini proxy error ${response.status}`;
      let raw = '';
      try {
        const errData = await response.json();
        raw = JSON.stringify(errData).slice(0, 300);
        errMsg = errData?.error?.message || errData?.error || errMsg;
      } catch {}
      console.error('[Gemini] Proxy HTTP', response.status, response.statusText,
        '| body:', raw, '| errMsg:', errMsg);
      throw new GeminiProxyError(errMsg, response.status);
    }

    const data = await response.json();
    return data?.candidates?.[0]?.content?.parts?.[0]?.text ?? null;
  }

  throw new GeminiProxyError('Gemini rate limit bereikt na meerdere pogingen', 429);
}

export async function extractAddressFromImage(
  base64Image: string
): Promise<ScanResult | null> {
  try {
    const text = await callGemini({
      contents: [
        {
          parts: [
            {
              inlineData: {
                mimeType: 'image/jpeg',
                data: base64Image,
              },
            },
            {
              text: 'Dit is een foto van één bezorgadres op een apotheeklabel of adressenlijst. Er is precies één adres zichtbaar in beeld (het adres in het midden van de foto). Het zijn gedrukte Nederlandse adressen in Latijnse tekens. Lees exact wat er staat — verzin niets en gebruik uitsluitend Latijnse letters (a-z), nooit niet-Latijnse tekens. Geef:\n- straatnaam (street)\n- huisnummer (houseNumber)\n- postcode (postalCode, formaat: 1234 AB)\n- stad (city)\n- apotheeknaam (pharmacyName) als zichtbaar, anders leeg\n\nGeen patiëntnamen, geen medicatienamen. Antwoord in JSON.',
            },
          ],
        },
      ],
      generationConfig: {
        temperature: 0,
        responseMimeType: 'application/json',
        responseSchema: {
          type: 'OBJECT',
          properties: {
            street:       { type: 'STRING' },
            houseNumber:  { type: 'STRING' },
            postalCode:   { type: 'STRING' },
            city:         { type: 'STRING' },
            pharmacyName: { type: 'STRING' },
          },
          required: ['street', 'houseNumber', 'postalCode', 'city'],
        },
      },
    });

    console.log('Gemini raw response (OCR):', text);
    if (!text) return null;
    const parsed = JSON.parse(text);
    return {
      address: {
        street:      parsed.street,
        houseNumber: parsed.houseNumber,
        postalCode:  parsed.postalCode,
        city:        parsed.city,
      },
      pharmacyName: parsed.pharmacyName || undefined,
    };
  } catch (err: any) {
    if (err?.message?.includes('API key not configured')) {
      alert('Configuratie fout: GEMINI_API_KEY ontbreekt op de server. Voeg hem toe via Netlify → Site settings → Environment variables.');
    }
    throw err; // let processQueue handle and display it
  }
}

// ── Patiënt-chatbot ─────────────────────────────────────────────────

const PATIENT_SYSTEM_PROMPT = `
Je bent een vriendelijke informatie-assistent van een apotheek.

WAT JE MAG DOEN:
- Algemene informatie geven over medicijnen (zoals in bijsluiters staat)
- Uitleggen wat een medicijn doet, voor welke klachten het gebruikt wordt
- Algemene bewaartips geven
- Uitleggen hoe een medicijn ingenomen wordt (algemeen, niet persoonlijk)
- Vragen beantwoorden over de bezorgstatus (zie hieronder hoe)
- Doorverwijzen naar de apotheker voor persoonlijke vragen
- Een terugbelverzoek aanbieden

BEZORGSTATUS — BELANGRIJK:
Er zijn geen ordernummers, trackingcodes of referentienummers in dit systeem.
Pakketstatus wordt uitsluitend opgezocht via postcode + huisnummer.
Als een patiënt vraagt naar hun bezorging, vraag dan nooit om een ordernummer of code.
Verwijs hen naar het Track & Trace-scherm bovenaan deze pagina en leg uit dat
ze daar hun postcode en huisnummer kunnen invoeren om de status te zien.

WAT JE NOOIT DOET — ABSOLUUT VERBODEN:
- Persoonlijk doseeradvies geven
- Medicijninteracties beoordelen voor een specifieke persoon
- Zeggen of iemand een medicijn wel of niet moet gebruiken
- Diagnoses stellen of bevestigen
- Oordelen over behandelingen van artsen
- Vragen om een ordernummer, trackingcode of referentienummer

BIJ PERSOONLIJK MEDISCH ADVIES:
Sluit je antwoord altijd af met:
"Voor persoonlijk advies kunt u het beste contact opnemen met uw apotheker of huisarts."

TOON:
Vriendelijk, rustig, beknopt. Geen medisch jargon tenzij de gebruiker dat zelf gebruikt. Altijd in het Nederlands.

Apotheek: [PHARMACY_INFO]
`.trim();

const RISK_KEYWORDS = [
  'overdosis', 'teveel ingenomen', 'zelfmoord', 'zelfdoding',
  'leven beëindigen', 'suïcide', 'pijnstillers allemaal',
  'niet meer willen leven', 'per ongeluk teveel',
];

export function detectRisk(text: string): boolean {
  const lower = text.toLowerCase();
  return RISK_KEYWORDS.some(kw => lower.includes(kw));
}

const EMERGENCY_RESPONSE =
  'Dit klinkt als een noodgeval. Bel direct **112** of bel **113** voor de Zelfmoordpreventielijn (24/7 bereikbaar, ook via chat op 113.nl). Geef deze informatie ook door aan iemand in uw omgeving.';

export async function answerPatientQuestion(
  question: string,
  history: ChatMessage[],
  pharmacyName: string
): Promise<{ text: string; hasRisk: boolean }> {
  // Risicodetectie vóór elke Gemini-aanroep
  if (detectRisk(question)) {
    return { text: EMERGENCY_RESPONSE, hasRisk: true };
  }

  const systemPrompt = PATIENT_SYSTEM_PROMPT.replace('[PHARMACY_INFO]', pharmacyName);

  // Gemini vereist afwisselende user/model beurt — map assistant → model
  const contents = [
    ...history.map(m => ({
      role: m.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: m.text }],
    })),
    { role: 'user', parts: [{ text: question }] },
  ];

  try {
    const text = await callGemini({
      systemInstruction: { parts: [{ text: systemPrompt }] },
      contents,
      generationConfig: { temperature: 0.3, maxOutputTokens: 1024 },
    });
    return { text: text ?? 'Er is iets misgegaan. Probeer het opnieuw.', hasRisk: false };
  } catch {
    return { text: 'Er is een fout opgetreden. Probeer het later opnieuw.', hasRisk: false };
  }
}

// ── Route-optimalisatie via Google Maps Routes API ───────────────────

export interface LatLng { lat: number; lng: number; }

export interface RouteGeometry {
  orderedIds:     string[];
  coords:         LatLng[];
  totalDistanceM: number;
  totalDurationS: number;
}

interface BatchResult {
  ids:       string[];
  coords:    LatLng[];
  distanceM: number;
  durationS: number;
  /** Laatste leg apart, zodat een geketend cluster de rit naar de kunstmatige
   *  bestemming (de centroïde van het volgende cluster) kan aftrekken. */
  lastLegDistanceM: number;
  lastLegDurationS: number;
}

export async function optimizeRouteDetailed(
  addresses: (Address & { id: string })[],
  startAddress?: string | null,
  endAddress?: string | null
): Promise<RouteGeometry> {
  if (addresses.length === 0) {
    return { orderedIds: [], coords: [], totalDistanceM: 0, totalDurationS: 0 };
  }

  // Groepeer op adres vóór de optimalisatie: zeven pakketten voor één deur zijn
  // één stop, want de koerier stapt één keer af. Zonder dit ging elk pakket als
  // eigen waypoint de Routes API in — dat vult de limiet van 25 per aanroep, en
  // de optimalisatie kon ze uit elkaar trekken.
  const groups = new Map<string, (Address & { id: string })[]>();
  for (const a of addresses) {
    const key = addressKey(a);
    const existing = groups.get(key);
    if (existing) existing.push(a);
    else groups.set(key, [a]);
  }

  // Het eerste pakket van een groep vertegenwoordigt het adres in de route.
  const representatives = [...groups.values()].map(g => g[0]);
  const groupByRepresentative = new Map<string, (Address & { id: string })[]>();
  for (const g of groups.values()) groupByRepresentative.set(g[0].id, g);

  /**
   * Zet de volgorde van vertegenwoordigers terug om naar pakket-ids, met de
   * leden van een groep aaneengesloten. Het vangnet achteraan garandeert dat er
   * nooit een pakket uit de route verdwijnt, wat er ook misgaat.
   */
  const expand = (orderedRepIds: string[]): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const repId of orderedRepIds) {
      const group = groupByRepresentative.get(repId);
      for (const p of group ?? []) {
        if (!seen.has(p.id)) { out.push(p.id); seen.add(p.id); }
      }
      if (!group && !seen.has(repId)) { out.push(repId); seen.add(repId); }
    }
    for (const a of addresses) {
      if (!seen.has(a.id)) { out.push(a.id); seen.add(a.id); }
    }
    return out;
  };

  console.log(`[Route] ${addresses.length} pakketten op ${representatives.length} adressen`);

  if (representatives.length === 1) {
    const a = representatives[0];
    const coords = a.lat != null && a.lng != null ? [{ lat: a.lat, lng: a.lng }] : [];
    return { orderedIds: expand([a.id]), coords, totalDistanceM: 0, totalDurationS: 0 };
  }

  try {
    const result = await optimizeBatch(representatives, startAddress, endAddress);
    const orderedIds = expand(result.ids);
    console.log('[Route] Geoptimaliseerde volgorde (fiets):');
    result.ids.forEach((id, i) => {
      const addr = addresses.find(a => a.id === id);
      const aantal = groupByRepresentative.get(id)?.length ?? 1;
      console.log(`  Stop ${i + 1}: ${addr?.street} ${addr?.houseNumber}, ${addr?.postalCode}${aantal > 1 ? ` (${aantal} pakketten)` : ''}`);
    });
    console.log(`[Route] Totaal: ${(result.distanceM / 1000).toFixed(1)} km · ${Math.round(result.durationS / 60)} min`);
    return { orderedIds, coords: result.coords, totalDistanceM: result.distanceM, totalDurationS: result.durationS };
  } catch (err) {
    console.error('[Route] Routes API mislukt, fallback op originele volgorde:', err);
    const coords = representatives.filter(a => a.lat != null && a.lng != null).map(a => ({ lat: a.lat!, lng: a.lng! }));
    return { orderedIds: expand(representatives.map(r => r.id)), coords, totalDistanceM: 0, totalDurationS: 0 };
  }
}

export async function optimizeRoute(
  addresses: (Address & { id: string })[],
  startAddress?: string | null,
  endAddress?: string | null
): Promise<string[]> {
  const { orderedIds } = await optimizeRouteDetailed(addresses, startAddress, endAddress);
  return orderedIds;
}

type GeoStop = Address & { id: string; lat: number; lng: number };

function clusterByGeography(stops: GeoStop[], maxClusterSize = 23): GeoStop[][] {
  const k = Math.ceil(stops.length / maxClusterSize);
  if (k === 1) return [stops];

  const centroids = stops
    .filter((_, i) => i % Math.floor(stops.length / k) === 0)
    .slice(0, k)
    .map(s => ({ lat: s.lat, lng: s.lng }));

  let assignments = new Array(stops.length).fill(0);
  for (let iter = 0; iter < 5; iter++) {
    assignments = stops.map(stop => {
      let minDist = Infinity, nearest = 0;
      centroids.forEach((c, ci) => {
        const d = (stop.lat - c.lat) ** 2 + (stop.lng - c.lng) ** 2;
        if (d < minDist) { minDist = d; nearest = ci; }
      });
      return nearest;
    });
    centroids.forEach((_, ci) => {
      const members = stops.filter((_, i) => assignments[i] === ci);
      if (!members.length) return;
      centroids[ci] = {
        lat: members.reduce((s, m) => s + m.lat, 0) / members.length,
        lng: members.reduce((s, m) => s + m.lng, 0) / members.length,
      };
    });
  }

  const clusters: GeoStop[][] = Array.from({ length: k }, () => []);
  stops.forEach((stop, i) => clusters[assignments[i]].push(stop));

  const result: GeoStop[][] = [];
  clusters.forEach(cluster => {
    if (cluster.length <= maxClusterSize) {
      result.push(cluster);
    } else {
      result.push(...clusterByGeography(cluster, maxClusterSize));
    }
  });

  return result.filter(c => c.length > 0);
}

/**
 * Herkent een startpunt in de vorm "lat,lng" (GPS-positie van de koerier) en geeft
 * de coördinaat terug. Een tekstadres (apotheek) levert null — dezelfde detectie als
 * in netlify/functions/maps.ts, die "lat,lng" als location.latLng doorstuurt.
 */
function parseLatLng(value?: string | null): LatLng | null {
  if (!value) return null;
  const m = value.trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/);
  return m ? { lat: parseFloat(m[1]), lng: parseFloat(m[2]) } : null;
}

/** Zwaartepunt van een cluster — gebruikt als bestemming voor het vorige cluster. */
function centroidOf(cluster: GeoStop[]): LatLng | null {
  if (cluster.length === 0) return null;
  return {
    lat: cluster.reduce((s, p) => s + p.lat, 0) / cluster.length,
    lng: cluster.reduce((s, p) => s + p.lng, 0) / cluster.length,
  };
}

function orderClusters(clusters: GeoStop[][], startCoord?: { lat: number; lng: number }): GeoStop[][] {
  if (clusters.length <= 1) return clusters;

  const centroids = clusters.map(c => ({
    lat: c.reduce((s, p) => s + p.lat, 0) / c.length,
    lng: c.reduce((s, p) => s + p.lng, 0) / c.length,
  }));

  const visited = new Set<number>();
  const ordered: number[] = [];
  let current = 0;

  if (startCoord) {
    let minDist = Infinity;
    centroids.forEach((c, i) => {
      const d = (c.lat - startCoord.lat) ** 2 + (c.lng - startCoord.lng) ** 2;
      if (d < minDist) { minDist = d; current = i; }
    });
  }

  visited.add(current);
  ordered.push(current);

  while (visited.size < clusters.length) {
    let nearest = -1, minDist = Infinity;
    centroids.forEach((c, i) => {
      if (visited.has(i)) return;
      const d = (c.lat - centroids[current].lat) ** 2 + (c.lng - centroids[current].lng) ** 2;
      if (d < minDist) { minDist = d; nearest = i; }
    });
    if (nearest === -1) break;
    visited.add(nearest);
    ordered.push(nearest);
    current = nearest;
  }

  return ordered.map(i => clusters[i]);
}

async function optimizeBatch(
  addresses: (Address & { id: string })[],
  startAddress?: string | null,
  endAddress?: string | null
): Promise<BatchResult> {
  if (addresses.length <= 25) {
    return await optimizeSingleBatch(addresses, startAddress, endAddress);
  }

  // Splits in reeds geocodeerde en nog te geocoderen adressen
  const cached   = addresses.filter(a => a.lat != null && a.lng != null);
  const uncached = addresses.filter(a => a.lat == null || a.lng == null);

  console.log(`[Route] ${cached.length} adressen hebben GPS, ${uncached.length} nog te geocoderen`);

  const withCoords: GeoStop[] = cached.map(a => ({ ...a, lat: a.lat!, lng: a.lng! }));

  if (uncached.length > 0) {
    console.log('[Route] Geocoderen van', uncached.length, 'ontbrekende adressen...');
    const geocodeResponse = await fetch('/.netlify/functions/maps', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({
        action: 'geocode',
        addresses: uncached.map(a =>
          `${a.street} ${a.houseNumber}, ${a.postalCode} ${a.city}, Netherlands`
        ),
      }),
    });
    const { results } = await geocodeResponse.json() as { results: ({ lat: number; lng: number } | null)[] };
    uncached.forEach((addr, i) => {
      if (results[i]) withCoords.push({ ...addr, lat: results[i]!.lat, lng: results[i]!.lng });
    });
  }

  // Herstel originele volgorde, houd adressen zonder coords apart als fallback
  const geoStops = addresses
    .map(a => withCoords.find(w => w.id === a.id))
    .filter((x): x is GeoStop => x !== null && x !== undefined);
  const missingIds = addresses.filter(a => !withCoords.find(w => w.id === a.id)).map(a => a.id);

  if (geoStops.length === 0) return { ids: addresses.map(a => a.id), coords: [], distanceM: 0, durationS: 0, lastLegDistanceM: 0, lastLegDurationS: 0 };

  // Clustervolgorde vanaf de GPS-positie van de koerier als die bekend is; bij een
  // tekstadres (apotheek) of geen startpunt terug naar het eerste adres in de lijst.
  const gpsStart        = parseLatLng(startAddress);
  const startCoord      = gpsStart ?? geoStops[0];
  const rawClusters     = clusterByGeography(geoStops, 23);
  const orderedClusters = orderClusters(rawClusters, startCoord);

  console.log('[Route] Clustervolgorde bepaald vanaf', gpsStart ? 'GPS-locatie koerier' : 'eerste adres', `(${startCoord.lat}, ${startCoord.lng})`);
  console.log('[Route]', orderedClusters.length, 'geografische clusters:', orderedClusters.map(c => c.length).join(' / '), 'stops');

  const allIds: string[] = [];
  const allCoords: LatLng[] = [];
  let distanceM = 0;
  let durationS = 0;
  // Elk volgend cluster start waar het vorige eindigde — niet opnieuw bij de
  // GPS-startlocatie, anders fietst de koerier tussen clusters heen en weer.
  let prevEndCoord: LatLng | null = null;
  for (let i = 0; i < orderedClusters.length; i++) {
    const isLast = i === orderedClusters.length - 1;

    const batchStart = i === 0
      ? startAddress
      : (prevEndCoord ? `${prevEndCoord.lat},${prevEndCoord.lng}` : null);

    // Tussenliggende clusters krijgen de centroïde van het VOLGENDE cluster als
    // bestemming. Zonder dat pinde optimizeSingleBatch het laatste adres uit de
    // k-means-volgorde vast als destination — een willekeurige plek, waardoor de
    // ketening technisch klopte maar de koerier op de verkeerde hoek uitkwam.
    // Bijkomend voordeel: met een externe bestemming gaan álle stops van het
    // cluster als waypoint mee in plaats van op één na.
    const nextCentroid = isLast ? null : centroidOf(orderedClusters[i + 1]);
    const batchEnd = isLast
      ? endAddress
      : (nextCentroid ? `${nextCentroid.lat},${nextCentroid.lng}` : null);

    const batch = await optimizeSingleBatch(orderedClusters[i], batchStart, batchEnd);
    allIds.push(...batch.ids);

    // Coords opschonen. Het eerste punt van een volgend cluster is de eindstop
    // van het vorige — die staat er al. Het laatste punt van een tussenliggend
    // cluster is de kunstmatige centroïde, geen stop; zonder dit maakt de lijn
    // op de kaart een sprong naar het midden van het volgende cluster en terug.
    let clusterCoords = batch.coords;
    if (i > 0 && clusterCoords.length > 0) clusterCoords = clusterCoords.slice(1);
    if (!isLast && clusterCoords.length > 0) clusterCoords = clusterCoords.slice(0, -1);
    allCoords.push(...clusterCoords);

    // Idem voor afstand en tijd: de rit naar de centroïde maakt de koerier niet.
    // Het echte stuk tussen twee clusters wordt geteld door de eerste leg van het
    // volgende cluster, dat immers op de eindstop van dit cluster begint.
    distanceM += batch.distanceM - (isLast ? 0 : batch.lastLegDistanceM);
    durationS += batch.durationS - (isLast ? 0 : batch.lastLegDurationS);

    // Eindpunt van dit cluster = laatste stop in de geoptimaliseerde volgorde
    const lastStop = orderedClusters[i].find(s => s.id === batch.ids[batch.ids.length - 1]);
    if (lastStop) prevEndCoord = { lat: lastStop.lat, lng: lastStop.lng };
  }
  return {
    ids: [...new Set([...allIds, ...missingIds])],
    coords: allCoords,
    distanceM: Math.max(0, distanceM),
    durationS: Math.max(0, durationS),
    lastLegDistanceM: 0,
    lastLegDurationS: 0,
  };
}

async function optimizeSingleBatch(
  addresses: (Address & { id: string })[],
  startAddress?: string | null,
  endAddress?: string | null
): Promise<BatchResult> {
  if (addresses.length <= 1) {
    const a = addresses[0];
    const coords = a?.lat != null && a?.lng != null ? [{ lat: a.lat, lng: a.lng }] : [];
    return { ids: addresses.map(x => x.id), coords, distanceM: 0, durationS: 0, lastLegDistanceM: 0, lastLegDurationS: 0 };
  }

  const formatAddress = (a: Address) =>
    `${a.street} ${a.houseNumber}, ${a.postalCode} ${a.city}, Netherlands`;

  // Als eindadres opgegeven: alle stops zijn waypoints (origin en destination zijn extern).
  // Als alleen startadres: alles behalve het laatste adres is waypoint.
  // Anders: eerste en laatste zijn origin/destination, de rest zijn waypoints.
  const hasExternalStart = !!startAddress;
  const hasExternalEnd   = !!endAddress;

  const origin      = startAddress ?? formatAddress(addresses[0]);
  const destination = endAddress   ?? formatAddress(addresses[addresses.length - 1]);

  const waypointAddresses = hasExternalEnd
    ? addresses                    // alle stops zijn waypoints
    : hasExternalStart
      ? addresses.slice(0, -1)     // alles behalve het laatste
      : addresses.slice(1, -1);    // alles behalve eerste én laatste

  const intermediates = waypointAddresses.map(a => formatAddress(a));

  console.log('[Route] Routes API aanroep voor', addresses.length, 'stops (fiets)...');

  const response = await fetch('/.netlify/functions/maps', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
    body: JSON.stringify({ origin, destination, intermediates }),
  });

  const data = await response.json();

  if (data.status !== 'OK') {
    throw new Error(`Routes API fout: ${data.status} — ${data.error_message ?? ''}`);
  }

  const order: number[] = data.order ?? [];
  console.log('[Route] Geoptimaliseerde waypoint-volgorde:', order);

  const reordered = hasExternalEnd
    ? order.map((i: number) => waypointAddresses[i].id)
    : hasExternalStart
      ? [
          ...order.map((i: number) => waypointAddresses[i].id),
          addresses[addresses.length - 1].id,
        ]
      : [
          addresses[0].id,
          ...order.map((i: number) => addresses[i + 1].id),
          addresses[addresses.length - 1].id,
        ];

  const coords: LatLng[] = (data.coords ?? []).map((p: any) => ({ lat: p.lat, lng: p.lng }));

  const legDistances: number[] = data.legDistancesM ?? [];
  const legDurations: number[] = data.legDurationsS ?? [];

  return {
    ids:       [...new Set(reordered)],
    coords,
    distanceM: data.distanceMeters  ?? 0,
    durationS: data.durationSeconds ?? 0,
    lastLegDistanceM: legDistances[legDistances.length - 1] ?? 0,
    lastLegDurationS: legDurations[legDurations.length - 1] ?? 0,
  };
}

// ── Adresvalidatie via PDOK Locatieserver (officieel BAG) ─────────────

export interface PdokResult {
  found:        boolean;
  street?:      string | null;
  city?:        string | null;
  postalCode?:  string | null;
  houseNumber?: string | null;
  lat?:         number | null;
  lng?:         number | null;
}

export async function validateAddressPDOK(postalCode: string, houseNumber: string): Promise<PdokResult> {
  try {
    const res = await fetch('/.netlify/functions/pdok', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
      body: JSON.stringify({ postalCode, houseNumber }),
    });
    if (!res.ok) return { found: false };
    return await res.json();
  } catch {
    return { found: false };
  }
}
